import { useState, useEffect } from "react"

import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material"
import ControlledTextInput from "@/components/controlledInputs/ControlledTextInput"
import { OutputFormat, setDefaults } from "react-geocode"

import { z } from "zod"
import { FieldError, SubmitHandler, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import ControlledDatePicker from "@/components/controlledInputs/ControlledDatePIcker"
import GoogleAutoAddress from "@/components/controlledInputs/ControlledGoogleAddress"
import FileUploader from "@/components/FileUploader"
import { PutObjectCommand } from "@aws-sdk/client-s3"
import { ImageType, s3, warmS3Credentials } from "@/common/globals"
import { AgeRangeEnum, ImageOrientationEnum } from "@/common/types"
import { FileWithPath } from "react-dropzone"
import ControlledCheckboxGroup from "@/components/controlledInputs/ControlledCheckBoxGroup"
import ControlledSwitch from "@/components/controlledInputs/ControlledSwitch"
import ControlledRadioGroup from "@/components/controlledInputs/ControlledRadioGroup"
import useCreateEvent from "@/hooks/api/events/useCreateEvent"
import RecurrenceSelector from "@/components/recurrence/RecurrenceSelector"

const createNewEventFormData = z
  .object({
    event_id: z.number().optional(),
    title: z
      .string()
      .min(1, { message: "The event title is required." })
      .max(255, { message: "The title is too long." }),
    description: z
      .string()
      .min(1, { message: "The event description is required." }),
    type: z.array(z.number()).refine((values) => values.length > 0, {
      message: "At least one option must be selected",
    }),
    address: z
      .string()
      .min(1, { message: "Please specify where the event will happen." })
      .max(500, { message: "The address is too long." }),
    location: z
      .string()
      .min(1, { message: "Please provide a location name." })
      .max(500, { message: "Location title is too long." }),
    event_start_timestamp: z.date({
      message: "Please indicate a start date for your event.",
    }),
    event_end_timestamp: z
      .date({
        message: "Please indicate a end date for your event.",
      })
      .optional(),
    button_label: z
      .string()
      .min(1, {
        message: "Please specify a button label for event submission.",
      })
      .max(30, { message: "" }),
    position: z.object({
      lat: z.number(),
      long: z.number(),
    }),
    price: z
      .string()
      .min(1, { message: "Must include a value for price," })
      .max(50, { message: "Price is too long." }),
    action_url: z
      .string()
      .min(1, { message: "Must include an action url." })
      .max(2000, { message: "Action url is too long." }),
    pet_friendly: z.boolean(),
    age_range: z.nativeEnum(AgeRangeEnum),
    images: z.array(
      z.object({
        file: z.custom<FileWithPath>(),
        orientation: z.nativeEnum(ImageOrientationEnum),
        order: z.number(),
      })
    ),
    recurrence: z
      .object({
        enabled: z.boolean(),
        frequency: z.enum(["daily", "weekly", "monthly"]),
        interval: z.number().min(1).max(12),
        daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
        endType: z.enum(["date", "count", "none"]),
        endDate: z.date().optional(),
        endCount: z.number().min(1).max(52).optional(),
      })
      .optional(),
  })
  .refine(
    (data) => {
      if (!data.event_end_timestamp) {
        return true
      } else {
        return data.event_start_timestamp <= data.event_end_timestamp
      }
    },
    {
      path: ["eventStart"],
      message: "Start date must be before end date.",
    }
  )
  .refine((data) => data.event_start_timestamp >= new Date(), {
    path: ["eventStart"],
    message: "Start date must start after now.",
  })
export type CreateNewEventFormDataType = z.infer<typeof createNewEventFormData>

const defaultValues: CreateNewEventFormDataType = {
  title: "",
  description: "",
  type: [] as number[],
  address: "",
  location: "",
  event_start_timestamp: new Date(),
  event_end_timestamp: undefined,
  button_label: "",
  position: { lat: 0, long: 0 },
  price: "0.00",
  action_url: "",
  pet_friendly: false,
  age_range: AgeRangeEnum.ANY,
  images: [],
  recurrence: {
    enabled: false,
    frequency: "weekly" as const,
    interval: 1,
    daysOfWeek: [],
    endType: "none" as const,
    endDate: undefined,
    endCount: undefined,
  },
}

type EventType = {
  event_type_id: number
  human_name: string
  machine_name: string
}

type SubmitResult = {
  success: boolean
  message: string
}

export const CreateEvent = () => {
  // const [selectedFiles, setSelectedFiles] = useState<ImageType[]>([])
  const [horizontalFiles, setHorizontalFiles] = useState<ImageType[]>([])
  const [verticalFiles, setVerticalFiles] = useState<ImageType[]>([])
  const { mutateAsync: createEvent } = useCreateEvent()

  const [submitMessageResult, setSubmitMessageResult] = useState(
    null as SubmitResult | null
  )
  const [isLoading, setLoading] = useState(false)
  const [eventTypes, setEventTypes] = useState(
    undefined as EventType[] | undefined
  )

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const response = await fetch("/api/events/types")
      const { eventTypes } = await response.json()

      if (mounted) {
        setLoading(false)
        const allowedTypes = eventTypes.filter(
          (type: EventType) =>
            !["attractions", "favorites"].includes(type.machine_name)
        )
        setEventTypes(allowedTypes)
      }
    })()

    return () => {
      mounted = false
    }
  }, [])

  // function updateEvent() {
  //   setEvent({ ...event });
  // }

  const {
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm<CreateNewEventFormDataType>({
    resolver: zodResolver(createNewEventFormData),
    defaultValues: defaultValues,
    mode: "onBlur",
  })

  const handleSetValue = (value: string) => {
    setValue("address", value)
  }

  const submitEvent: SubmitHandler<CreateNewEventFormDataType> = async (
    data
  ) => {
    setLoading(true)

    // convert the lat, long
    setDefaults({
      key: import.meta.env.VITE_GOOGLE_MAP_KEY, // Your API key here.
      language: "en", // Default language for responses.
      region: "us", // Default region for responses.
      outputFormat: OutputFormat.JSON,
    })

    const geocoder = new window.google.maps.Geocoder()
    await geocoder.geocode({ address: data.address }, (results, status) => {
      if (status === "OK" && results) {
        console.log(results[0].geometry.location)
        const lat = results[0].geometry.location.lat()
        const lng = results[0].geometry.location.lng()
        data["position"] = { lat: lat, long: lng }
      } else {
        console.error("Geocoding failed:", status)
      }
    })
    // await fromAddress(data.address)
    //   .then(({ results }) => {
    //     const { lat, lng } = results[0].geometry.location
    //     data["position"] = { lat: lat, long: lng }
    //   })
    //   .catch(console.error)

    const allImages = horizontalFiles.concat(verticalFiles)

    // Convert File objects to plain objects for JSON serialization
    // (File properties aren't enumerable and won't survive JSON.stringify)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data.images = allImages.map((image) => ({
      file: {
        name: image.file.name,
        path: image.file.path,
        lastModified: image.file.lastModified,
        size: image.file.size,
        type: image.file.type,
      },
      orientation: image.orientation,
      order: image.order,
    })) as any

    // Ensure S3 credentials are ready before uploading
    await warmS3Credentials()

    await createEvent(data).then(async (res) => {
      const event = res.data
      // if (response.ok) {
      // once event is created in db, then add images to s3
      if (event && allImages.length > 0) {
        for (const file of allImages) {
          const params = {
            Bucket: `${import.meta.env.VITE_AWS_EVENT_BUCKET}`,
            Key: `${event.insertId}/${file.file.name}`,
            Body: file.file,
            ContentType: file.file.type,
          }

          try {
            await s3.send(new PutObjectCommand(params))
          } catch (err) {
            console.error("There was a problem uploading images", err)
          }
        }
        // }

        const eventCount = event.occurrenceCount || 1
        setSubmitMessageResult({
          success: true,
          message:
            eventCount > 1
              ? `Successfully created ${eventCount} recurring events`
              : `Successfully created new event #${event.insertId}`,
        })
      }
    })

    reset()
    setHorizontalFiles([])
    setVerticalFiles([])
    setLoading(false)
    // } else {
    //   setSubmitMessageResult({
    //     success: false,
    //     message: `Failed to create event: ${event.error ?? "failed"}`,
    //   })
    //   setLoading(false)
  }

  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  // Utility function to get the first error message with the field name
  const getFirstErrorMessage = (
    errors: Record<string, FieldError | undefined>
  ): string | undefined => {
    for (const [key, value] of Object.entries(errors)) {
      if (value) {
        console.error(key)
        return `${value.message}`
      }
    }
    return undefined
  }

  useEffect(() => {
    // handle errors
    const firstErrorMessage = getFirstErrorMessage(Object(errors))
    setErrorMessage(firstErrorMessage)
  }, [errors])

  const ageRangeOptions = Object.values(AgeRangeEnum).map((type) => ({
    label: type
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char: string) => char.toUpperCase()),
    value: type,
  }))

  return (
    <>
      <Box>
        <>
          {submitMessageResult && (
            <Alert
              severity={submitMessageResult.success ? "success" : "error"}
              sx={{ marginBottom: "1rem" }}
            >
              {submitMessageResult.message}
            </Alert>
          )}
          {!isLoading && eventTypes ? (
            <form onSubmit={handleSubmit(submitEvent)}>
              <ControlledCheckboxGroup
                control={control}
                name="type"
                label="Event Type"
                options={eventTypes.map((element) => {
                  return {
                    label: element.human_name,
                    value: element.event_type_id,
                  }
                })}
              />
              <Stack spacing={1}>
                <ControlledTextInput
                  variant="outlined"
                  control={control}
                  name="title"
                  label="Event title"
                  type="text"
                />
                <ControlledTextInput
                  control={control}
                  name="description"
                  label="Event description"
                  type="text"
                />
                <GoogleAutoAddress
                  control={control}
                  name="address"
                  label="Event Address"
                  type="text"
                  setValue={handleSetValue}
                />
                <ControlledTextInput
                  control={control}
                  name="location"
                  label="Location Title"
                  type="text"
                />
                <ControlledDatePicker
                  label="Start Date and Time"
                  control={control}
                  name="event_start_timestamp"
                />
                <ControlledDatePicker
                  label="End Date and Time (optional)"
                  control={control}
                  name="event_end_timestamp"
                />
                <RecurrenceSelector control={control} name="recurrence" />
                <ControlledTextInput
                  control={control}
                  name="price"
                  label="Price"
                  type="text"
                />
                <ControlledTextInput
                  control={control}
                  name="button_label"
                  label="Button Label"
                  type="text"
                />
                <ControlledTextInput
                  control={control}
                  name="action_url"
                  label="Action Url"
                />
                <ControlledSwitch
                  control={control}
                  name="pet_friendly"
                  label="Pet Friendly"
                />
                <ControlledRadioGroup
                  control={control}
                  name="age_range"
                  label="Age Range"
                  options={ageRangeOptions}
                />
                <FileUploader
                  horizontalFiles={horizontalFiles}
                  setHorizontalFiles={setHorizontalFiles}
                  verticalFiles={verticalFiles}
                  setVerticalFiles={setVerticalFiles}
                />
              </Stack>
              <Box sx={{ textAlign: "center", padding: "1rem" }}>
                {errorMessage && (
                  <Typography variant="caption" color="text.warning">
                    {errorMessage}
                  </Typography>
                )}
              </Box>
              <Button
                variant="contained"
                // disabled={notReadyToSubmit}
                type="submit"
              >
                Create Event
              </Button>
            </form>
          ) : (
            <Box
              sx={{ display: "flex", width: "100%", boxSizing: "border-box" }}
              paddingTop={8}
              justifyContent="center"
            >
              <CircularProgress />
            </Box>
          )}
        </>
      </Box>
    </>
  )
}
